import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Hero from './components/Hero';
import CategoryDropdown from './components/CategoryDropdown';
import CategoryGrid from './components/CategoryGrid';
import ArticlesSection from './components/ArticlesSection';
import Footer from './components/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL;

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'YOYBUY Spreadsheet',
  url: `${siteUrl}/`,
  description: 'YOYBUY Spreadsheet — лучший инструмент для организации покупок из Китая. Таблицы для TaoBao, 1688, сравнение агентов, расчёт стоимости доставки. Полное руководство по шоппингу из Китая 2026.',
  inLanguage: 'ru',
  publisher: {
    '@type': 'Organization',
    name: 'YOYBUY Spreadsheet',
    url: `${siteUrl}/`,
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg',
    },
  },
};

export default function Home() {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <main className="min-h-screen bg-background-50">
      <JsonLd data={websiteSchema as unknown as Record<string, unknown>} />
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/80 backdrop-blur-md border-b border-background-200/40">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className="text-lg md:text-xl font-heading font-bold text-foreground-900 whitespace-nowrap">
              {t('site.title')}
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap">
              {t('nav.home')}
            </Link>
            <a href="#articles-section" className="text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap">
              {t('nav.shoppingGuide')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-shopping-cart-2-line w-4 h-4 flex items-center justify-center"></i>
              {t('nav.buyNow')}
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg text-foreground-800 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
            aria-label={t('nav.menu')}
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl w-6 h-6 flex items-center justify-center`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/40 animate-in slide-in-from-top-2 duration-200">
            <div className="px-4 py-4 flex flex-col gap-3">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors duration-150 cursor-pointer"
              >
                <i className="ri-home-4-line w-5 h-5 flex items-center justify-center text-accent-500"></i>
                {t('nav.home')}
              </Link>
              <a
                href="#articles-section"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors duration-150 cursor-pointer"
              >
                <i className="ri-book-open-line w-5 h-5 flex items-center justify-center text-accent-500"></i>
                {t('nav.shoppingGuide')}
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors duration-150 cursor-pointer"
              >
                <i className="ri-table-line w-5 h-5 flex items-center justify-center text-accent-500"></i>
                {t('nav.yoybuySheet')}
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-shopping-cart-2-line w-4 h-4 flex items-center justify-center"></i>
                {t('nav.buyNow')}
              </a>
            </div>
          </div>
        )}
      </nav>

      <Hero />
      <CategoryDropdown />
      <CategoryGrid />
      <ArticlesSection />

      <section className="w-full py-14 md:py-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
            {t('site.subtitle')}
          </h2>
          <p className="text-sm md:text-base text-foreground-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            {t('site.description')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-base hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-table-line text-lg w-5 h-5 flex items-center justify-center"></i>
              {t('hero.ctaYoybuy')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border-2 border-primary-500 text-primary-600 font-semibold text-base hover:bg-primary-50 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-shopping-cart-2-line text-lg w-5 h-5 flex items-center justify-center"></i>
              {t('hero.ctaBuyNow')}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}